import { BaseStreamRegistry } from './base'
import { DEFAULT_RETRY_CONFIG } from '../config'
import { ENDPOINTS } from '@shared/utils/constant'
import { StreamKey, type StreamHandlers } from '../types'
import { TradeExecutionSide } from '@injectivelabs/sdk-ts/types'
import { StreamManagerV2 } from '@injectivelabs/sdk-ts/client/indexer'
import { IndexerGrpcOracleStreamV2 } from '@injectivelabs/sdk-ts/client/indexer'
import { IndexerGrpcDerivativesStreamV2 } from '@injectivelabs/sdk-ts/client/indexer'
import type {
  DerivativeOrdersStreamCallbackV2,
  DerivativeTradesStreamCallbackV2,
  OraclePricesByMarketsStreamCallbackV2,
  DerivativeOrderHistoryStreamCallbackV2,
  DerivativeOrderbookUpdateStreamCallbackV2
} from '@injectivelabs/sdk-ts'

const derivativesMarketStreamV2 = new IndexerGrpcDerivativesStreamV2(
  ENDPOINTS.indexer
)
const oracleStreamV2 = new IndexerGrpcOracleStreamV2(ENDPOINTS.indexer)

export class DerivativeStreamRegistry extends BaseStreamRegistry {
  subscribeDerivativeOrderHistory(
    subaccountId: string,
    marketId: string | undefined,
    handlers: StreamHandlers<
      Parameters<DerivativeOrderHistoryStreamCallbackV2>[0]
    >
  ): void {
    const streamId = StreamKey.DerivativeOrderHistory

    const manager = new StreamManagerV2({
      id: streamId,
      streamFactory: () => {
        return derivativesMarketStreamV2.streamOrdersHistory({
          subaccountId,
          ...(marketId && { marketId }),
          callback: (response) => manager.emit('data', response)
        })
      },
      onData: handlers.onData,
      retryConfig: DEFAULT_RETRY_CONFIG
    })

    this.registerStream(streamId, manager, handlers)
  }

  subscribeDerivativeSubaccountTrades(
    subaccountId: string,
    marketId: string | undefined,
    handlers: StreamHandlers<Parameters<DerivativeTradesStreamCallbackV2>[0]>
  ): void {
    const streamId = StreamKey.DerivativeTradesSubaccount

    const manager = new StreamManagerV2({
      id: streamId,
      streamFactory: () => {
        return derivativesMarketStreamV2.streamTrades({
          subaccountId,
          ...(marketId && { marketId }),
          callback: (response) => manager.emit('data', response)
        })
      },
      onData: handlers.onData,
      retryConfig: DEFAULT_RETRY_CONFIG
    })

    this.registerStream(streamId, manager, handlers)
  }

  subscribeDerivativeOrders(
    subaccountId: string,
    marketId: string | undefined,
    handlers: StreamHandlers<Parameters<DerivativeOrdersStreamCallbackV2>[0]>
  ): void {
    const streamId = StreamKey.DerivativeOrders

    const manager = new StreamManagerV2({
      id: streamId,
      streamFactory: () => {
        return derivativesMarketStreamV2.streamOrders({
          subaccountId,
          ...(marketId && { marketId }),
          callback: (response) => manager.emit('data', response)
        })
      },
      onData: handlers.onData,
      retryConfig: DEFAULT_RETRY_CONFIG
    })
    this.registerStream(streamId, manager, handlers)
  }

  subscribeDerivativeTrades(
    marketId: string,
    handlers: StreamHandlers<Parameters<DerivativeTradesStreamCallbackV2>[0]>
  ): void {
    const streamId = StreamKey.DerivativeTrades

    const manager = new StreamManagerV2({
      id: streamId,
      streamFactory: () => {
        return derivativesMarketStreamV2.streamTrades({
          marketId,
          executionSide: TradeExecutionSide.Taker,
          callback: (response) => manager.emit('data', response)
        })
      },
      onData: handlers.onData,
      retryConfig: DEFAULT_RETRY_CONFIG
    })
    this.registerStream(streamId, manager, handlers)
  }

  subscribeDerivativeOrderbook(
    marketId: string,
    handlers: StreamHandlers<
      Parameters<DerivativeOrderbookUpdateStreamCallbackV2>[0]
    >
  ): void {
    const streamId = StreamKey.Orderbook

    const manager = new StreamManagerV2({
      id: streamId,
      streamFactory: () => {
        return derivativesMarketStreamV2.streamOrderbookUpdates({
          marketIds: [marketId],
          callback: (response) => manager.emit('data', response)
        })
      },
      onData: handlers.onData,
      retryConfig: DEFAULT_RETRY_CONFIG
    })
    this.registerStream(streamId, manager, handlers)
  }

  subscribeMarkPrices(
    marketIds: string[],
    handlers: StreamHandlers<
      Parameters<OraclePricesByMarketsStreamCallbackV2>[0]
    >
  ): void {
    const streamId = StreamKey.MarkPrices

    const manager = new StreamManagerV2({
      id: streamId,
      streamFactory: () => {
        return oracleStreamV2.streamOraclePricesByMarkets({
          marketIds,
          callback: (response) => manager.emit('data', response)
        })
      },
      onData: handlers.onData,
      retryConfig: DEFAULT_RETRY_CONFIG
    })
    this.registerStream(streamId, manager, handlers)
  }

  unsubscribeDerivativeSubaccountTrades(): void {
    this.unsubscribe(StreamKey.DerivativeTradesSubaccount)
  }

  unsubscribeDerivativeOrderHistory(): void {
    this.unsubscribe(StreamKey.DerivativeOrderHistory)
  }

  unsubscribeDerivativeOrders(): void {
    this.unsubscribe(StreamKey.DerivativeOrders)
  }

  unsubscribeDerivativeTrades(): void {
    this.unsubscribe(StreamKey.DerivativeTrades)
  }

  unsubscribeDerivativeOrderbook(): void {
    this.unsubscribe(StreamKey.Orderbook)
  }

  unsubscribeMarkPrices(): void {
    this.unsubscribe(StreamKey.MarkPrices)
  }
}
